import { ChevronDown } from 'lucide-react';
import { ComponentProps, forwardRef } from 'react';
import type { Input2Size, Input2Tone } from 'v5/design-system/Input2';
import {
  InputGroup2,
  InputGroup2Button,
  InputGroup2Input
} from 'v5/design-system/InputGroup2';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from 'v5/design-system/Select';
import { cn } from 'v5/platform/dom/cn';

export type AmountCurrencyOption = { code: string; name: string };

export type AmountUnitOption = {
  value: string;
  /** Shown in the list: "Per day". */
  optionLabel: string;
  /** Shown after the amount: "/ day". */
  shortLabel: string;
  /** Read by screen readers: "per day". */
  spokenLabel: string;
};

export type AmountWithUnitInputProps = Omit<
  ComponentProps<'input'>,
  'size' | 'value' | 'onChange'
> & {
  /** Amount as typed (see `useDraftNumber`). */
  value: string;
  onChange(event: { target: { value: string } }): void;
  currency: string;
  currencies: ReadonlyArray<AmountCurrencyOption>;
  onCurrencyChange(code: string): void;
  unit: string;
  units: ReadonlyArray<AmountUnitOption>;
  onUnitChange(unit: string): void;
  /** Accessible names of the two selects, e.g. "Currency" and "Billing unit". */
  currencyLabel: string;
  unitLabel: string;
  tone?: Input2Tone;
  size?: Input2Size;
  invalid?: boolean;
};

/**
 * Currency, amount and billing unit as one control that reads like the rate
 * it produces: `CAD ▾ | 850 | / day ▾`.
 *
 * Behaves like a text input towards forms: `id`, `aria-*`, `name`, `onBlur`
 * and the ref go to the amount `<input>`, so a `FormControl` / `FormLabel`
 * pair labels the amount and the two selects carry their own names. The
 * currency shows its ISO code (USD, CAD and AUD would all be "$"); the full
 * name is in the list and in what a screen reader announces.
 */
export const AmountWithUnitInput = forwardRef<
  HTMLInputElement,
  AmountWithUnitInputProps
>(function AmountWithUnitInput(
  {
    currency,
    currencies,
    onCurrencyChange,
    unit,
    units,
    onUnitChange,
    currencyLabel,
    unitLabel,
    tone = 'regular',
    size = 'md',
    invalid = false,
    readOnly,
    className,
    ...inputProps
  },
  ref
) {
  const selectedCurrency = currencies.find(option => option.code === currency);
  const selectedUnit = units.find(option => option.value === unit);
  // Bordered tone: the two selects sit on grey segments either side of the
  // amount, so the typing area is obvious and the ends read as choices.
  const segment =
    tone === 'regular'
      ? 'bg-bg-subtle not-disabled:xhover:bg-brand-50'
      : undefined;

  return (
    <InputGroup2
      tone={tone}
      size={size}
      variant={invalid ? 'danger' : 'default'}
      aria-readonly={readOnly}
      className={cn('min-w-0', className)}
    >
      <Select
        value={currency}
        onValueChange={onCurrencyChange}
        disabled={readOnly}
      >
        <SelectTrigger asChild variant="headless" aria-label={currencyLabel}>
          <InputGroup2Button
            className={cn(
              'shrink-0 gap-1 font-bold tracking-wide',
              segment,
              segment && 'border-r border-border-default'
            )}
          >
            <SelectValue>
              <span aria-hidden="true">{currency}</span>
              <span className="sr-only">
                {selectedCurrency?.name ?? currency}
              </span>
            </SelectValue>
            <ChevronDown aria-hidden="true" className="opacity-50" />
          </InputGroup2Button>
        </SelectTrigger>
        <SelectContent>
          {currencies.map(option => (
            <SelectItem key={option.code} value={option.code}>
              <span className="w-10 shrink-0 font-semibold">{option.code}</span>
              <span className="text-fg-muted">{option.name}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <InputGroup2Input
        ref={ref}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        enterKeyHint="done"
        readOnly={readOnly}
        className="min-w-[7ch] flex-1 px-2.5 font-bold tabular-nums"
        {...inputProps}
      />
      <Select value={unit} onValueChange={onUnitChange} disabled={readOnly}>
        <SelectTrigger asChild variant="headless" aria-label={unitLabel}>
          <InputGroup2Button
            className={cn(
              'min-w-22 shrink-0 justify-between gap-1 font-semibold text-fg-muted',
              segment,
              segment && 'border-l border-border-default'
            )}
          >
            <SelectValue>
              <span aria-hidden="true">{selectedUnit?.shortLabel ?? unit}</span>
              <span className="sr-only">
                {selectedUnit?.spokenLabel ?? unit}
              </span>
            </SelectValue>
            <ChevronDown aria-hidden="true" className="opacity-50" />
          </InputGroup2Button>
        </SelectTrigger>
        <SelectContent>
          {units.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.optionLabel}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </InputGroup2>
  );
});
